import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback, useMemo } from "react";

import Box from "@mui/material/Box";

import { makeStyles } from "tss-react/mui";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";

import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import ComponentSuspense from "../../Common/ComponentSuspense";
import Columns from "./Columns";
import SampleData from "./SampleData";

const HighChart = React.lazy(() => import("../DataExplorer/SideBar/Chart"));

const useStyles = makeStyles()((theme) => ({
  TablePaper: {
    background: "#ffffff",
    borderRadius: "10px",
    "& ::-webkit-scrollbar-track": {
      marginTop: "36px",
    },
  },
}));

const Statistics = ({
  Workspace_id,
  fromPopup,
  dropdownLoading,
  tableName,
  setDisableDropdown = () => {},
  schemaName = "MODEL",
}) => {
  const { classes } = useStyles();

  const [chartLoading, setChartLoading] = useState(false);

  const [state, setState] = useState({
    selectedRow: "",
    selectedRowType: "",
    currentTableName: "",
  });

  const { selectedRow, selectedRowType, currentTableName } = state;

  useEffect(() => {
    setDisableDropdown(chartLoading);
    return () => {};
  }, [chartLoading, setDisableDropdown]);

  const isTableSame = useMemo(
    () => currentTableName.toLowerCase() === tableName.toLowerCase(),
    [currentTableName, tableName]
  );

  const displayChart = useCallback(
    () => (
      <ComponentSuspense>
        <HighChart
          heading={selectedRow}
          selectedColumnType={selectedRowType}
          schemaName={schemaName}
          tableName={tableName}
          Workspace_id={Workspace_id}
          isTableSame={isTableSame}
          setChartLoading={setChartLoading}
        />
      </ComponentSuspense>
    ),
    [Workspace_id, isTableSame, schemaName, selectedRow, selectedRowType, tableName]
  );

  if (dropdownLoading || !tableName)
    return (
      <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
        <CircularProgress color="primary" />
      </Backdrop>
    );

  return (
    <Grid container spacing={1} sx={{ mb: 2 }}>
      <Grid
        sx={{ width: "100%", display: "flex", alignItems: "center" }}
        size={{
          xs: 12,
          md: fromPopup ? 12 : 6
        }}>
        <Box
          elevation={2}
          className={classes.TablePaper}
          sx={{ width: "100%", minHeight: "fit-content", ...(fromPopup ? { maxHeight: 250 } : {}) }}
          pl={1}
          component={fromPopup ? Paper : Box}
        >
          <Columns
            Workspace_id={Workspace_id}
            fromPopup={fromPopup}
            tableName={tableName}
            schemaName={schemaName}
            selectedRow={selectedRow}
            setState={setState}
          />
        </Box>
      </Grid>
      <Grid
        sx={{ width: "100%", display: "flex", alignItems: "center" }}
        size={{
          xs: 12,
          md: fromPopup ? 12 : 6
        }}>
        <Box
          xs={12}
          sx={{ px: "10px", minHeight: "300px", width: "100%", background: "#ffffff", borderRadius: "10px" }}
          elevation={2}
          component={fromPopup ? Paper : Box}
        >
          {displayChart()}
        </Box>
      </Grid>
      {!fromPopup && <SampleData Workspace_id={Workspace_id} tableName={tableName} schemaName={schemaName} />}
    </Grid>
  );
};

Statistics.propTypes = {
  Workspace_id: PropTypes.string,
  dropdownLoading: PropTypes.bool,
  fromPopup: PropTypes.bool,
  schemaName: PropTypes.string,
  setDisableDropdown: PropTypes.func,
  tableName: PropTypes.string,
};

export default Statistics;
